import {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    FC,
    MutableRefObject,
    PropsWithChildren,
    ReactElement,
    MouseEvent,
} from "react";
import {Link, LinkProps} from "react-router";
import classNames from "classnames";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";

export type FrameOneButtonType =
    LinkProps
    | AnchorHTMLAttributes<HTMLAnchorElement>
    | ButtonHTMLAttributes<HTMLButtonElement>;

export type FrameOneButtonRefType =
    HTMLAnchorElement
    | HTMLButtonElement;

export type FrameOneButtonColor =
    "purple"
    | "purple-outline"
    | "purple-text-no-border"
    | "purple-gradient"
    | "purple-gradient-dark"
    | "purple-blue-gradient"
    | "purple-blue-gradient-shadow"
    | "purple-outline-transparent"
    | "devourplay-gradient"
    | "devourplay-purple"
    | "gray"
    | "gray-light"
    | "ghost"
    | "white-drop-shadow"
    | "white-drop-shadow-devour"
    | "warning"
    | "danger"
    | "danger-outline"
    | "white-outline"
    | "dark-outline"
    | "dark"
    | "brand-dark"
    | "brand-light"
    | "success"
    | "twitter";

export type FrameOneButtonSize =
    "normal"
    | "large"
    | "narrow"
    | "pill"
    | "viewMode"
    | "icon" // Intended to be used with a single icon & no children.
    | "icon-square"; // Intended to be used with a single icon & no children.

interface Props<T extends FrameOneButtonType, U extends FrameOneButtonRefType> {
    color?: FrameOneButtonColor; // Manually set allowed colors for specific styling; update _frame-one-button.scss file to implement.
    size: FrameOneButtonSize;
    leftIcon?: FC;
    rightIcon?: FC;
    className?: string;

    to?: string; // When present, this component renders a <Link/> tag from react-router-dom and acts like a link to same-site pages. Otherwise, behaves like a regular button. Overrides presence of href prop.
    href?: string; // When present, this component renders a <a/> tag. Otherwise, behaves like a regular button.
    onClick?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void; // onClick action for the "button". Should be applied no matter if a <Link/>, <a/>, or <button/> is rendered.
    forwardRef?: MutableRefObject<U>;
    forwardProps?: T; // Props forwarded to <Link/> / <a/> / <button/> component.
    showSpinner?: boolean;
    spinnerLoadingText?: string; // If `showSpinner` is true, this will be the text beside it
    showSpinnerWithoutGlobalLoading?: boolean;
}

function FrameButton<T extends FrameOneButtonType, U extends FrameOneButtonRefType = HTMLButtonElement>({
    color,
    size,
    leftIcon,
    rightIcon,
    className,
    to,
    href,
    onClick,
    forwardRef,
    forwardProps,
    showSpinner,
    showSpinnerWithoutGlobalLoading,
    children,
    spinnerLoadingText = "Loading",
}: PropsWithChildren<Props<T, U>>): ReactElement {

    const LeftIcon: any = leftIcon;
    const RightIcon: any = rightIcon;

    const isLoading = useSelector((store: IStore) => store.metaStore.loadingIncrement);
    const disabled = isLoading > 0 || (forwardProps as ButtonHTMLAttributes<HTMLButtonElement>)?.disabled;

    function onClickHelper(event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>): void {
        if (onClick) {
            onClick(event);
        }
        event.stopPropagation();
    }

    // Generate the className for the button to be rendered, so we can assign it easily below in each branch of logic.
    const fullClassNames: string = classNames(
        "frame-one-button",
        `frame-one-button_size_${size}`,
        className,
        {
            "frame-one-button_disabled": disabled,
            [`frame-one-button_color_${color}`]: color,
        },
    );

    // Generate the content here so we can selectively use it in the return logic below.
    const content: ReactElement =
		<>
		    {leftIcon && <LeftIcon className="frame-one-button_left-icon"/>}
		    {children && <span>{children}</span>}
		    {rightIcon && <RightIcon className="frame-one-button_right-icon"/>}
		</>;
    if (to) {
        return (
            <Link
                {...forwardProps as LinkProps}
                to={to}
                onClick={onClickHelper}
                className={fullClassNames}
                ref={forwardRef as MutableRefObject<HTMLAnchorElement>}
            >
                {content}
            </Link>
        );
    } if (href) {
        return (
            <a
                {...forwardProps as AnchorHTMLAttributes<HTMLAnchorElement>}
                href={href}
                onClick={onClickHelper}
                target="_blank"
                rel="noopener noreferrer"
                className={fullClassNames}
                ref={forwardRef as MutableRefObject<HTMLAnchorElement>}
            >
                {content}
            </a>
        );
    }
    return (
        <button
            {...forwardProps as ButtonHTMLAttributes<HTMLButtonElement>}
            onClick={onClickHelper}
            className={fullClassNames}
            ref={forwardRef as MutableRefObject<HTMLButtonElement>}
            disabled={disabled}
        >
            {showSpinner && isLoading > 0 || showSpinnerWithoutGlobalLoading
                ? <div className="frame-one-button_loading">
                    {spinnerLoadingText &&
                        <p className="frame-one-button_loading_text">
                            {spinnerLoadingText}
                        </p>
                    }
                    <div className="loading-spinner"/>
                </div>
                : content}
        </button>

    );

}

export default FrameButton;
